import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { usersService } from '@/services/users.service';
import { inspectionService } from '@/services/inspection.service';

export type ForceTransferDialogMode = 'assign' | 'reassign' | 'force-transfer';

interface ForceTransferDialogProps {
  inspectionId: string;
  open: boolean;
  onClose: () => void;
  /**
   * Cosmetic only — every mode calls the exact same forceTransfer() mutation
   * with identical validation and audit/history recording (see
   * docs/OPS_ENGINEER_ASSIGNMENT_CENTER_DESIGN.md §5). Only the title, banner
   * copy, and submit button label change.
   */
  mode?: ForceTransferDialogMode;
}

const MODE_COPY: Record<
  ForceTransferDialogMode,
  { title: string; banner: string; submitLabel: string }
> = {
  assign: {
    title: 'Assign Engineer',
    banner:
      'This assigns an engineer to this job. A full ownership history record will be created.',
    submitLabel: 'Assign',
  },
  reassign: {
    title: 'Reassign Engineer',
    banner:
      'This reassigns the job to a different engineer. The current engineer will be notified and a full ownership history record will be created.',
    submitLabel: 'Reassign',
  },
  'force-transfer': {
    title: 'Force Transfer Inspection',
    banner:
      "Force transfer immediately changes ownership without the receiving engineer's consent. Both engineers will be notified and a full audit record will be created. Use only when necessary.",
    submitLabel: 'Force Transfer',
  },
};

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function ForceTransferDialog({
  inspectionId,
  open,
  onClose,
  mode = 'force-transfer',
}: ForceTransferDialogProps) {
  const copy = MODE_COPY[mode];
  const qc = useQueryClient();
  const [toEngineerId, setToEngineerId] = useState('');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  const { data: users = [] } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => usersService.listAll(),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      inspectionService.forceTransfer(inspectionId, { toEngineerId, reason, comment }),
    onSuccess: (res) => {
      toast.success(res.message);
      void qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      setToEngineerId('');
      setReason('');
      setComment('');
      onClose();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to force transfer')),
  });

  const canSubmit =
    toEngineerId &&
    reason.trim().length >= 10 &&
    comment.trim().length >= 10 &&
    !mutation.isPending;

  return (
    <Modal open={open} onClose={onClose} title={copy.title}>
      <div className="space-y-4">
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-sm font-medium text-destructive">Administrative Action</p>
          <p className="mt-1 text-xs text-muted-foreground">{copy.banner}</p>
        </div>

        <FormField label="Transfer To" htmlFor="force-transfer-to" required>
          <select
            id="force-transfer-to"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            value={toEngineerId}
            onChange={(e) => setToEngineerId(e.target.value)}
          >
            <option value="">— Select engineer —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Business Reason" htmlFor="force-transfer-reason" required>
          <Textarea
            id="force-transfer-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="State the business reason for this forced transfer..."
            rows={3}
          />
          <span className="text-xs text-muted-foreground">Minimum 10 characters</span>
        </FormField>

        <FormField label="Administrative Comment" htmlFor="force-transfer-comment" required>
          <Textarea
            id="force-transfer-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any additional context or notes for the audit record..."
            rows={3}
          />
          <span className="text-xs text-muted-foreground">
            Minimum 10 characters · recorded permanently in audit log
          </span>
        </FormField>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? 'Processing…' : copy.submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
